import { useTranslations } from 'use-intl';
import InfiniteScroll from '../../modules/InfiniteScroll';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import Table from '../../modules/Table';
import Icon, { Icons } from '../../elements/Icon';
import { QueryStatus } from '@tanstack/react-query';
import PoolsTableItem from './PoolsTableItem';
import { LiquidityPool } from 'nftx.js';

const LIMIT = 10;

const PoolsTable = ({
  header,
  pools = [],
  status,
  border,
}: {
  header?: ReactNode;
  pools?: LiquidityPool[];
  status: QueryStatus;
  border?: boolean;
}) => {
  const [visible, setVisible] = useState(LIMIT);
  const t = useTranslations('PoolsTable');

  const [sortAccessor, setSortAccessor] = useState<string | undefined>();
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sortedPools = useMemo(() => {
    if (!sortAccessor) {
      return pools;
    }

    return pools?.slice().sort((a: any, b: any) => {
      let av = a[sortAccessor];
      let bv = b[sortAccessor];
      if (sortAccessor === 'apr') {
        av = av?.['1m'];
        bv = bv?.['1m'];
      }
      if (sortDir === 'desc') {
        [av, bv] = [bv, av];
      }
      return Number(av - bv);
    });
  }, [pools, sortAccessor, sortDir]);

  const handleSort = (accessor: string, direction: 'asc' | 'desc') => {
    setSortAccessor(accessor);
    setSortDir(direction);
  };

  useEffect(() => {
    setSortAccessor(undefined);
    setSortDir('asc');
  }, [pools]);

  const visiblePools = sortedPools?.slice(0, visible);
  const showMorePools = visiblePools.length < sortedPools.length;

  return (
    <Table
      border={border}
      header={header}
      onSort={handleSort}
      headings={[
        { label: t('#'), align: 'left' },
        { label: t('pool'), align: 'left', className: 'w-full' },
        {
          label: t('apr'),
          accessor: 'apr',
        },
        {
          label: t('tvl'),
          accessor: 'totalValueLocked',
        },
        {
          label: t('vol24'),
          accessor: 'dailyTotalVolume',
        },
        {
          label: t('vol7d'),
          accessor: 'weeklyTotalVolume',
        },
        {
          label: t('active'),
          accessor: 'inRangeLiquidity',
          tooltip: 'total amount of liquidity that is in range',
        },
      ]}
    >
      {status === 'loading' && (
        <tr>
          <td colSpan={8} className="p-20 text-center text-gray-500">
            <Icon
              name={Icons.SPINNER_GAP}
              className="animate animate-spin inline-flex"
              size={48}
            />
            <p className="mt-4">{t('loading')}</p>
          </td>
        </tr>
      )}
      {status === 'success' && (
        <InfiniteScroll
          onLoadMore={() => setVisible((x) => x + LIMIT)}
          scrollable={showMorePools}
          loaderElement="tr"
          loaderClassName="table-row"
          loaderChildren={
            <td colSpan={7} className="p-3 text-center text-gray-500">
              <Icon
                name={Icons.SPINNER_GAP}
                className="animate animate-spin inline-flex"
                size={20}
              />
            </td>
          }
        >
          {visiblePools?.map((pool, i) => {
            return <PoolsTableItem key={pool.id} {...pool} index={i + 1} />;
          })}
        </InfiniteScroll>
      )}
    </Table>
  );
};

export default PoolsTable;
